import { PaletteMode, Components, Theme } from '@mui/material';
import { themePalette } from '../theme.palette';
import { typographyTokens } from '../typography-tokens';
import dimensions from '../dimensions';

// MuiDateRangePickerDay - Day component in date range picker
export const MuiDateRangePickerDayTheme = (
  mode: PaletteMode,
): Components<Theme>['MuiDateRangePickerDay'] => {
  const { componentTokens } = themePalette(mode);
  const { spacing } = dimensions;

  return {
    styleOverrides: {
      root: {
        fontSize: typographyTokens.fontSizes.xs,
        lineHeight: typographyTokens.lineHeights.xs,
        fontWeight: typographyTokens.fontWeights.semibold,
        backgroundColor: 'transparent',
        '&.MuiPickersSlideTransition-root': {
          overflowX: 'auto',
        },
        '&.MuiDateRangePickerDay-rangeIntervalDayHighlight .MuiDateRangePickerDay-rangeIntervalPreview button.MuiDateRangePickerDay-day':
          {
            backgroundColor: componentTokens.calendar.colors.date.background.range,
            color: componentTokens.calendar.colors.date.text.resting,
            borderColor: componentTokens.calendar.colors.date.stroke.resting,
            '&:hover': {
              backgroundColor: componentTokens.calendar.colors.date.background.hover,
              borderColor: componentTokens.calendar.colors.date.stroke.hover,
              color: componentTokens.calendar.colors.date.text.hover,
            },
            '&:focus': {
              backgroundColor: componentTokens.calendar.colors.date.background.hover,
              borderColor: componentTokens.calendar.colors.date.stroke.hover,
              color: componentTokens.calendar.colors.date.text.hover,
            },
            '&:disabled': {
              backgroundColor: componentTokens.calendar.colors.date.background.resting,
              borderColor: componentTokens.calendar.colors.date.stroke.resting,
              color: componentTokens.calendar.colors.date.text.resting,
              opacity: 0.4,
            },
          },
        '&.MuiDateRangePickerDay-rangeIntervalDayHighlightStart button.MuiDateRangePickerDay-day': {
          backgroundColor: `${componentTokens.calendar.colors.date.background.selected} !important`,
          color: `${componentTokens.calendar.colors.date.text.selected} !important`,
        },
        '&.MuiDateRangePickerDay-rangeIntervalDayHighlightEnd button.MuiDateRangePickerDay-day': {
          backgroundColor: `${componentTokens.calendar.colors.date.background.selected} !important`,
          color: `${componentTokens.calendar.colors.date.text.selected} !important`,
        },
      },
      day: {
        height: '32px',
        width: '32px',
        borderRadius: spacing(1), // 4px
        backgroundColor: componentTokens.calendar.colors.date.background.resting,
        borderColor: componentTokens.calendar.colors.date.stroke.resting,
        border: 'none',
        '&:hover': {
          backgroundColor: componentTokens.calendar.colors.date.background.hover,
        },
        '&:focus': {
          backgroundColor: componentTokens.calendar.colors.date.background.selected,
        },
        '&.Mui-selected': {
          backgroundColor: componentTokens.calendar.colors.date.background.selected,
          color: componentTokens.calendar.colors.date.text.selected,
          fontWeight: typographyTokens.fontWeights.semibold,
          '&:hover': {
            backgroundColor: componentTokens.calendar.colors.date.background.hover,
            color: componentTokens.calendar.colors.date.text.hover,
          },
          '&:focus': {
            backgroundColor: componentTokens.calendar.colors.date.background.selected,
            color: componentTokens.calendar.colors.date.text.selected,
          },
          '&:disabled': {
            backgroundColor: componentTokens.calendar.colors.date.background.resting,
            color: componentTokens.calendar.colors.date.text.resting,
            opacity: 0.4,
          },
        },
        '&.MuiPickersDay-today': {
          border: '1px solid',
          backgroundColor: componentTokens.calendar.colors.date.background.today,
          color: componentTokens.calendar.colors.date.text.today,
          borderColor: componentTokens.calendar.colors.date.stroke.today,
          '&:hover': {
            backgroundColor: componentTokens.calendar.colors.date.background.hover,
            borderColor: componentTokens.calendar.colors.date.stroke.hover,
            color: componentTokens.calendar.colors.date.text.hover,
          },
          '&:focus': {
            backgroundColor: componentTokens.calendar.colors.date.background.selected,
            borderColor: componentTokens.calendar.colors.date.stroke.selected,
            color: componentTokens.calendar.colors.date.text.selected,
          },
          '&:disabled': {
            backgroundColor: componentTokens.calendar.colors.date.background.resting,
            color: componentTokens.calendar.colors.date.text.resting,
            opacity: 0.4,
          },
          '&:selected': {
            backgroundColor: componentTokens.calendar.colors.date.background.selected,
            color: componentTokens.calendar.colors.date.text.selected,
            borderColor: componentTokens.calendar.colors.date.stroke.selected,
          },
        },
      },
      dayOutsideRangeInterval: {
        '&:hover': {
          border: 'none',
          backgroundColor: componentTokens.calendar.colors.date.background.hover,
        },
      },
      rangeIntervalPreview: {
        border: 'none',
        backgroundColor: 'transparent',
      },
    },
  };
};

// MuiDateRangeCalendar - The calendar root component for date range picker
export const MuiDateRangeCalendarTheme = (
  mode: PaletteMode,
): Components<Theme>['MuiDateRangeCalendar'] => {
  const { componentTokens, tokens } = themePalette(mode);
  const { spacing } = dimensions;

  return {
    styleOverrides: {
      root: {
        padding: spacing(4),
        gap: spacing(2),
        border: '1px solid',
        borderTopRightRadius: '6px',
        borderBottom: 'none',
        backgroundColor: componentTokens.calendar.colors.container.background.default,
        borderColor: componentTokens.calendar.colors.container.stroke.default,
        // Disable border between calendar containers
        '& > div:not(:last-of-type)': {
          borderRight: 'none',
        },
        // Target MUI's generated container class
        '& [class*="MuiDateRangeCalendar-container"]:not(:last-of-type)': {
          borderRight: 'none',
        },
        '& .MuiDayCalendar-header': {
          borderTop: '1px solid',
          borderColor: componentTokens.calendar.colors.container.stroke.default,
          '& .MuiTypography-root': {
            color: componentTokens.calendar.colors.date.text.today,
          },
        },
        '& .MuiDateRangeCalendar-monthContainer .MuiButton-root': {
          minWidth: 'auto',
          padding: '6px',
        },
        // Target the action bar (MuiDialogActions with MuiPickersLayout-actionBar modifier)
        '.MuiPickersLayout-actionBar': {
          display: 'flex',
          justifyContent: 'space-between',
        },
        '& .MuiDayCalendar-weekContainer': {
          gap: spacing(2),
          margin: spacing(2),
          // show extra highlight between days in selection range
          '& .MuiDateRangePickerDay-rangeIntervalDayHighlight .MuiDateRangePickerDay-day': {
            position: 'relative',
            border: '0 !important',
            '&::after': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              bottom: 0,
              right: '-4.5px',
              backgroundColor: tokens.background.secondary[3],
            },
          },
          // do not show extra highlight on last days of the week
          '& .MuiDateRangePickerDay-rangeIntervalDayHighlight:last-of-type .MuiDateRangePickerDay-day':
            {
              '&::after': {
                right: '0 !important',
                borderTopRightRadius: spacing(1),
                borderBottomRightRadius: spacing(1),
              },
            },
          // round corners of the left side on first days of the week
          '& .MuiDateRangePickerDay-rangeIntervalDayHighlight:first-of-type .MuiDateRangePickerDay-day':
            {
              '&::after': {
                borderTopLeftRadius: spacing(1),
                borderBottomLeftRadius: spacing(1),
              },
            },
          // round corners of the left side of the selection range start
          '& .MuiDateRangePickerDay-rangeIntervalDayHighlightStart .MuiDateRangePickerDay-day': {
            '&::after': {
              borderTopLeftRadius: spacing(1),
              borderBottomLeftRadius: spacing(1),
            },
          },
          // do not show extra highlight on last day of selection range
          '& .MuiDateRangePickerDay-rangeIntervalDayHighlightEnd .MuiDateRangePickerDay-day': {
            '&::after': {
              right: `0 !important`,
              borderTopRightRadius: spacing(1),
              borderBottomRightRadius: spacing(1),
            },
          },
          // do not show extra highlight on last visible cell of the month
          '& .MuiDateRangePickerDay-lastVisibleCell .MuiDateRangePickerDay-day': {
            '&::after': {
              right: '0 !important',
              borderTopRightRadius: spacing(1),
              borderBottomRightRadius: spacing(1),
            },
          },
          // round corners of the left side on first visible cell of the month
          '& .MuiDateRangePickerDay-firstVisibleCell .MuiDateRangePickerDay-day': {
            '&::after': {
              borderTopLeftRadius: spacing(1),
              borderBottomLeftRadius: spacing(1),
            },
          },
        },
      },
    },
  };
};
